import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosInstance from "@helper/interceptor";
import Spinner from "@components/Spinner/Spinner";
import { Card, Container } from "react-bootstrap";
import ServerError from "@components/Error/ServerError";
import KpiItem from "./KpiItem";
import HealthPieChart from "./HeathPieChart";
import TopSpeciesChart from "./TopSpeciesChart";
import SensorTypeChart from "./SensorTypeChart";
import TaskPeriodPieChart from "./TaskPeriodPieChart";
import { useTranslation } from "react-i18next";
import { useProjectFilter } from "../../context/ProjectFilterContext";
import InfoBox from "@components/InfoBox/InfoBox";
import {
  useBackgroundProjects,
  useBackgroundSensors,
  useBackgroundTrees,
  useBackgroundTreeSpecies,
} from "@hooks/useBackgroundData";
import { getAllTasks } from "@modules/Tasks/services/taskCrud";
import {
  BatteryLowIcon,
  CalendarWeekIcon,
  ClipboardDataIcon,
  LineChartIcon,
  PlugDisconnectedIcon,
  SensorsIcon,
  TreesIcon,
  AreaIcon,
  WrenchIcon,
} from "@components/icons";
import "./Dashboard.scss";

const WITHOUT_PROJECT = "without-project";
const isWithoutProject = (item) => item?.project_id == null;

export default function Dashboard() {
  const [criticalSensorCount, setCriticalSensorCount] = useState(0);
  const [sensorCount, setSensorCount] = useState(0);
  const [treeStatus, setTreeStatus] = useState({});
  const [treeCount, setTreeCount] = useState(0);
  const [disconnectedCount, setDisconnectedCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const { t, i18n } = useTranslation();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const user = useSelector((state) => state.auth?.user);
  const { trees, isLoading: isTreesLoading } = useBackgroundTrees();
  const { sensors, isLoading: isSensorsLoading } = useBackgroundSensors();
  const { treeSpecies } = useBackgroundTreeSpecies();
  const { projects } = useBackgroundProjects();

  const {
    data: dashboardResponse,
    isLoading,
    isError,
  } = useQuery("dashboard", async () => await axiosInstance("/dashboard"), {
    refetchOnWindowFocus: false,
    retry: false,
  });

  const navigate = useNavigate();
  const { data: tasksResponse, isLoading: isTasksLoading } = useQuery(
    "dashboard-tasks",
    () => getAllTasks(),
    {
      refetchOnWindowFocus: false,
    }
  );
  // Task suggestions are temporarily disabled in the UI.

  useEffect(() => {
    if (!dashboardResponse) return;
    const payload = dashboardResponse?.data ?? dashboardResponse ?? {};
    const trees = payload?.trees ?? 0;
    const sensors = payload?.sensors ?? 0;
    const health_state = payload?.health_state ?? {};
    const sensors_critical_battery = payload?.sensors_critical_battery ?? 0;
    const sensors_not_send_last_3h = payload?.sensors_not_send_last_3h ?? 0;
    const updatedAt =
      payload?.last_updated || payload?.updated_at || payload?.timestamp || payload?.date;
    setTreeCount(trees);
    setSensorCount(sensors);
    setTreeStatus(health_state);
    setCriticalSensorCount(sensors_critical_battery);
    setDisconnectedCount(sensors_not_send_last_3h);
    setLastUpdated(updatedAt ? new Date(updatedAt) : new Date());
  }, [dashboardResponse]);

  const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return "–";
    }
    return new Intl.NumberFormat(i18n.language, options).format(value);
  };

  const formatTimestamp = (dateValue) => {
    if (!dateValue) return "–";
    const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return "–";
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(parsedDate);
  };

  const projectsInCity = useMemo(() => {
    if (!selectedCity) return projects || [];
    return (projects || []).filter((project) => {
      const city = project?.city || project?.location;
      return city === selectedCity;
    });
  }, [projects, selectedCity]);

  useEffect(() => {
    if (
      selectedProjectId &&
      selectedProjectId !== WITHOUT_PROJECT &&
      !projectsInCity.some((project) => Number(project.id) === Number(selectedProjectId))
    ) {
      setSelectedProjectId(null);
    }
  }, [projectsInCity, selectedProjectId, setSelectedProjectId]);

  const filteredTrees = useMemo(() => {
    if (!Array.isArray(trees)) return [];
    return trees.filter((tree) => {
      // Handle "without-project" filter
      if (selectedProjectId === WITHOUT_PROJECT) {
        return isWithoutProject(tree);
      }
      // Handle regular project filter
      if (selectedProjectId && tree?.project_id !== selectedProjectId) return false;
      if (selectedCity) {
        const project = (projects || []).find((p) => p.id === tree?.project_id);
        const projectCity = project?.city || project?.location;
        if (projectCity !== selectedCity) return false;
      }
      return true;
    });
  }, [trees, selectedProjectId, selectedCity, projects]);

  const visibleTreeIds = useMemo(
    () => new Set((filteredTrees || []).map((tree) => Number(tree.id)).filter(Number.isFinite)),
    [filteredTrees]
  );

  const filteredSensors = useMemo(() => {
    if (!Array.isArray(sensors)) return [];
    return sensors.filter((sensor) => {
      // Handle "without-project" filter
      if (selectedProjectId === WITHOUT_PROJECT) {
        return isWithoutProject(sensor);
      }
      // Check project filter
      if (selectedProjectId && sensor.project_id !== selectedProjectId) return false;

      // Check city filter by looking up the project
      if (selectedCity) {
        const project = (projects || []).find((p) => p.id === sensor.project_id);
        const projectCity = project?.city || project?.location;
        if (projectCity !== selectedCity) return false;
      }

      return true;
    });
  }, [sensors, selectedProjectId, selectedCity, projects]);

  const hasTreeData = Array.isArray(trees) && !isTreesLoading;
  const hasSensorData = Array.isArray(sensors) && !isSensorsLoading;
  const isFilterActive = Boolean(selectedCity || selectedProjectId);

  const averageValue = (values) => {
    const numericValues = values
      .map((value) => {
        if (value === null || value === undefined || value === "") return null;
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : null;
      })
      .filter((value) => value !== null);
    if (!numericValues.length) return null;
    const total = numericValues.reduce((sum, value) => sum + value, 0);
    return total / numericValues.length;
  };

  const normalizeSensorState = (state) => {
    if (state === null || state === undefined || state === "") return null;
    const numericState = Number(state);
    return Number.isFinite(numericState) ? numericState : null;
  };

  const inferSensorType = (sensor) => {
    if (sensor?.pulse_sensor) return "pulse_sensor";
    if (sensor?.soil_moisture_sensor) return "FDR";
    if (sensor?.soil_water_tension_sensor) {
      if (sensor.soil_water_tension_sensor.model === "TE") return "TEROS21";
      return "Watermark";
    }
    if (sensor?.dendrometer_sensor) return "dendrometer_sensor";
  };

  const treeCountValue = hasTreeData ? filteredTrees.length : isFilterActive ? null : treeCount;
  const sensorCountValue = hasSensorData
    ? filteredSensors.length
    : isFilterActive
    ? null
    : sensorCount;

  const treeSpeciesCount = hasTreeData
    ? new Set(filteredTrees.map((tree) => tree?.species_id || tree?.species_name).filter(Boolean))
        .size
    : isFilterActive
    ? null
    : treeSpecies?.length ?? null;

  const maintenanceNeededCountValue = hasSensorData
    ? filteredSensors.filter((s) => {
        const state = normalizeSensorState(s?.sensor_state);
        return state === 1 || state === 2;
      }).length
    : isFilterActive
    ? null
    : null;

  const avgHeight = averageValue(filteredTrees.map((tree) => tree?.height));
  const avgTrunkCircumference = averageValue(
    filteredTrees.map((tree) => {
      const rawCircumference = tree?.trunk_circumference;
      if (rawCircumference === null || rawCircumference === undefined || rawCircumference === "") {
        return null;
      }
      const circumference = Number(rawCircumference);
      if (!Number.isFinite(circumference)) return null;
      return circumference;
    })
  );
  const avgCrownDiameter = averageValue(filteredTrees.map((tree) => tree?.crown_diameter));
  const avgAge = averageValue(
    filteredTrees.map((tree) => {
      const rawYear = tree?.planting_year;
      if (rawYear === null || rawYear === undefined || rawYear === "") return null;
      const year = Number(rawYear);
      const currentYear = new Date().getFullYear();
      if (!Number.isFinite(year) || year > currentYear) return null;
      return currentYear - year;
    })
  );

  const criticalFromSensors = filteredSensors.filter((sensor) => {
    const rawBattery = sensor?.battery;
    if (rawBattery === null || rawBattery === undefined || rawBattery === "") {
      return normalizeSensorState(sensor?.sensor_state) === 1;
    }
    const battery = Number(rawBattery);
    if (Number.isFinite(battery)) {
      return battery < 30;
    }
    return normalizeSensorState(sensor?.sensor_state) === 1;
  }).length;
  const disconnectedFromSensors = filteredSensors.filter(
    (sensor) => normalizeSensorState(sensor?.sensor_state) === 3
  ).length;

  const criticalCountValue = hasSensorData
    ? criticalFromSensors
    : isFilterActive
    ? null
    : criticalSensorCount;
  const disconnectedCountValue = hasSensorData
    ? disconnectedFromSensors
    : isFilterActive
    ? null
    : disconnectedCount;

  const topSpecies = useMemo(() => {
    if (!hasTreeData) return [];
    const speciesMap = new Map();
    filteredTrees.forEach((tree) => {
      const speciesName = tree?.species_name || tree?.species_id;
      if (speciesName && speciesName !== "Unbekannt") {
        speciesMap.set(speciesName, (speciesMap.get(speciesName) || 0) + 1);
      }
    });
    return Array.from(speciesMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  }, [filteredTrees, hasTreeData]);

  const treeHealthCounts = useMemo(() => {
    // Always compute from frontend data when available
    if (!hasTreeData) return null;

    const counts = {
      healthy: 0,
      drying_out: 0,
      dry: 0,
      unknown: 0,
      no_sensor: 0,
      no_data: 0,
      maintenance_needed: 0,
      frost: 0,
      too_wet: 0,
    };

    filteredTrees.forEach((tree) => {
      const statusRaw = tree?.health_state;
      const status = typeof statusRaw === "number" ? String(statusRaw) : statusRaw;

      // Check for unknown state (3) and differentiate
      if (status === 3 || status === "3" || status === "unknown") {
        if (!tree.has_active_sensor) {
          counts.no_sensor += 1;
        } else {
          const sensorState = normalizeSensorState(tree.active_sensor_state);
          // sensor_state: 0=working, 1=battery empty, 2=maintenance needed, 3=no data
          if (sensorState === 3) {
            counts.no_data += 1;
          } else if (sensorState === 2 || sensorState === 1) {
            counts.maintenance_needed += 1;
          } else {
            counts.no_data += 1;
          }
        }
        return;
      }

      switch (status) {
        case 0:
        case "0":
        case "healthy":
          counts.healthy += 1;
          break;
        case 1:
        case "1":
        case "drying_out":
          counts.drying_out += 1;
          break;
        case 2:
        case "2":
        case "dry":
          counts.dry += 1;
          break;
        case 4:
        case "4":
        case "frost":
          counts.frost += 1;
          break;
        case 5:
        case "5":
        case "wet":
        case "too_wet":
          counts.too_wet += 1;
          break;
        default:
          break;
      }
    });
    return counts;
  }, [filteredTrees, hasTreeData]);

  const treeStatusValue = treeHealthCounts;

  const sensorTypeLabels = {
    pulse_sensor: "Pulse",
    soil_moisture_sensor: "Soil Moisture",
    soil_water_tension_sensor: "Soil Water Tension",
    dendrometer_sensor: "Dendrometer",
    unknown: "Unknown",
  };

  const sensorTypeBreakdown = useMemo(() => {
    if (!hasSensorData) return [];
    const counts = new Map();
    filteredSensors.forEach((sensor) => {
      const typeKey = inferSensorType(sensor);
      if (!typeKey) return;
      counts.set(typeKey, (counts.get(typeKey) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        label: sensorTypeLabels[type] || String(type),
        count,
      }));
  }, [filteredSensors, hasSensorData]);

  const parseTaskDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const normalizeTaskStatus = (status) => {
    if (!status) return "planned";
    const normalized = String(status).replace(/_/g, "-").toLowerCase();
    if (normalized === "completed" || normalized === "done") return "completed";
    return "planned";
  };

  const taskPeriodCounts = useMemo(() => {
    const rawTasks = Array.isArray(tasksResponse)
      ? tasksResponse
      : tasksResponse?.results || tasksResponse?.data || [];
    if (!rawTasks.length) {
      return { today: 0, this_week: 0, this_month: 0, overdue: 0 };
    }

    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = (() => {
      const d = new Date(dayStart);
      const weekday = d.getDay();
      const diff = weekday === 0 ? -6 : 1 - weekday;
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      return d;
    })();
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    const month = now.getMonth();
    const year = now.getFullYear();

    const counts = { today: 0, this_week: 0, this_month: 0, overdue: 0 };

    rawTasks.forEach((task) => {
      const treeId = Number(task.treeId || task.tree_id || task.tree?.id);
      if ((selectedProjectId || selectedCity) && !visibleTreeIds.has(treeId)) {
        return;
      }

      const dueDate = parseTaskDate(
        task.scheduledAt || task.scheduled_at || task.dueDate || task.due_date || task.dueDateTime
      );
      if (!dueDate) return;

      const status = normalizeTaskStatus(task.status);

      const isToday =
        dueDate.getFullYear() === dayStart.getFullYear() &&
        dueDate.getMonth() === dayStart.getMonth() &&
        dueDate.getDate() === dayStart.getDate();
      const isOverdue = status !== "completed" && dueDate < dayStart;
      const isThisWeek = dueDate >= weekStart && dueDate <= weekEnd;
      const isThisMonth = dueDate.getFullYear() === year && dueDate.getMonth() === month;

      if (isOverdue) {
        counts.overdue += 1;
        return;
      }
      if (isToday) {
        counts.today += 1;
        return;
      }
      if (isThisWeek) {
        counts.this_week += 1;
        return;
      }
      if (isThisMonth) {
        counts.this_month += 1;
      }
    });

    return counts;
  }, [tasksResponse, selectedProjectId, selectedCity, visibleTreeIds]);

  const inventoryCards = [
    {
      id: "trees",
      title: t("dashboard.kpi_total_trees"),
      value: formatNumber(treeCountValue),
      icon: <TreesIcon size="20px" color="#ffffff" />,
      link: "/trees?from=dashboard",
      linkTitle: t("dashboard.view_trees"),
    },
    {
      id: "tree-species",
      title: t("dashboard.kpi_total_species"),
      value: formatNumber(treeSpeciesCount),
      icon: <ClipboardDataIcon size="20px" color="#ffffff" />,
    },
  ];

  const biometricCards = [
    {
      id: "avg-height",
      title: t("dashboard.kpi_avg_height"),
      value: formatNumber(avgHeight, { maximumFractionDigits: 1 }),
      unit: "m",
      icon: <LineChartIcon size="20px" color="#ffffff" />,
    },
    {
      id: "avg-trunk-circumference",
      title: t("dashboard.kpi_avg_trunk_circumference"),
      value: formatNumber(avgTrunkCircumference, { maximumFractionDigits: 1 }),
      unit: "cm",
      icon: <AreaIcon size="20px" color="#ffffff" />,
    },
    {
      id: "avg-crown-diameter",
      title: t("dashboard.kpi_avg_crown_diameter"),
      value: formatNumber(avgCrownDiameter, { maximumFractionDigits: 1 }),
      unit: "m",
      icon: <AreaIcon size="20px" color="#ffffff" />,
    },
    {
      id: "avg-age",
      title: t("dashboard.kpi_avg_age"),
      value: formatNumber(avgAge, { maximumFractionDigits: 0 }),
      unit: t("dashboard.years_short"),
      icon: <CalendarWeekIcon size="20px" color="#ffffff" />,
    },
  ];

  const sensorCards = [
    {
      id: "sensors",
      title: t("dashboard.kpi_total_sensors"),
      value: formatNumber(sensorCountValue),
      icon: <SensorsIcon size="20px" color="#ffffff" />,
      link: "/sensors?from=dashboard",
      linkTitle: t("dashboard.view_sensors"),
    },
    {
      id: "maintenance-sensors",
      title: t("dashboard.kpi_maintenance_sensors"),
      value: formatNumber(maintenanceNeededCountValue),
      icon: <WrenchIcon size="20px" color="#ffffff" />,
    },
    {
      id: "critical-sensors",
      title: t("dashboard.kpi_critical_sensors"),
      value: formatNumber(criticalCountValue),
      icon: <BatteryLowIcon size="20px" color="#ffffff" />,
    },
    {
      id: "disconnected-sensors",
      title: t("dashboard.kpi_disconnected_sensors"),
      value: formatNumber(disconnectedCountValue),
      icon: <PlugDisconnectedIcon size="20px" color="#ffffff" />,
    },
  ];

  // Early returns AFTER all hooks have been called
  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError) return <ServerError />;

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <Container fluid>
          <div className="dashboard-header-inner">
            <div>
              <div className="dashboard-subtitle">
                {t("dashboard.data_status")}: {formatTimestamp(lastUpdated)}
              </div>
            </div>
            <div className="dashboard-controls" />
          </div>
        </Container>
      </div>

      <div className="dashboard-content" data-tour="dashboard-content">
        <Container fluid>
          <div className="dashboard-kpi-groups">
            <Card className="dashboard-panel dashboard-kpi-group" data-tour="dashboard-inventory">
              <Card.Header className="dashboard-panel-header">
                <h6 className="mb-0">{t("dashboard.group_inventory")}</h6>
              </Card.Header>
              <Card.Body>
                <div className="dashboard-kpi-group-grid">
                  {inventoryCards.map((card) => (
                    <KpiItem key={card.id} {...card} />
                  ))}
                </div>
              </Card.Body>
            </Card>

            <Card className="dashboard-panel dashboard-kpi-group" data-tour="dashboard-biometrics">
              <Card.Header className="dashboard-panel-header">
                <h6 className="mb-0">{t("dashboard.group_biometrics")}</h6>
              </Card.Header>
              <Card.Body>
                <div className="dashboard-kpi-group-grid">
                  {biometricCards.map((card) => (
                    <KpiItem key={card.id} {...card} />
                  ))}
                </div>
              </Card.Body>
            </Card>

            <Card className="dashboard-panel dashboard-kpi-group" data-tour="dashboard-sensors">
              <Card.Header className="dashboard-panel-header">
                <h6 className="mb-0">{t("dashboard.group_sensors")}</h6>
              </Card.Header>
              <Card.Body>
                <div className="dashboard-kpi-group-grid">
                  {sensorCards.map((card) => (
                    <KpiItem key={card.id} {...card} />
                  ))}
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="dashboard-panels">
            <div data-tour="dashboard-health">
              <HealthPieChart treeStatus={treeStatusValue} />
            </div>
            <div data-tour="dashboard-tasks">
              <TaskPeriodPieChart
                data={taskPeriodCounts}
                isLoading={isTasksLoading}
                onOpenTasks={() => navigate("/tasks?from=dashboard")}
              />
            </div>

            <div className="dashboard-panel-stack">
              <Card className="dashboard-panel">
                <Card.Header className="dashboard-panel-header">
                  <h6 className="mb-0">{t("dashboard.top_species")}</h6>
                  <InfoBox
                    infoTitle={t("dashboard.top_species_info_title")}
                    infoContent={<p className="mb-0">{t("dashboard.top_species_info_body")}</p>}
                  />
                </Card.Header>
                <Card.Body>
                  <TopSpeciesChart data={topSpecies} total={treeCountValue} />
                </Card.Body>
              </Card>

              {sensorTypeBreakdown.length ? (
                <Card className="dashboard-panel">
                  <Card.Header className="dashboard-panel-header">
                    <h6 className="mb-0">{t("dashboard.sensor_types")}</h6>
                  </Card.Header>
                  <Card.Body>
                    <SensorTypeChart data={sensorTypeBreakdown} />
                  </Card.Body>
                </Card>
              ) : null}
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}
